import React, { useRef, useEffect } from "react"
import { ResponsiveLine } from "@nivo/line"
import useIsInViewport from "use-is-in-viewport"
import { FigureText } from "./FigureText"
import { Source } from "./Source"
// Obtient une interpolation linéaire entre 2 valeurs
const themeColor = "#e82e34"

const tooltip = ({ data, xTooltipLabel, yTooltipLabel, color }) => {
  return (
    <div
      style={{ background: "white" }}
      className="text-base p-2 shadow-lg border-red-400 border-2"
    >
      <div>{xTooltipLabel(data.x)}</div>
      <div>{yTooltipLabel(data.y)}</div>
    </div>
  )
}

const theme = {
  fontSize: "0.8rem",
  axis: {
    legend: {
      text: {
        fontSize: "1rem",
      },
    },
  },
}

const DashedLine = ({ series, lineGenerator, xScale, yScale, parentRef }) => {
  const pathRef = useRef(null)

  useEffect(() => {
    // Get a reference to the <path>
    var path = pathRef.current

    // Get length of path... ~577px in this case
    var pathLength = path.getTotalLength()

    // Make very long dashes (the length of the path itself)
    path.style.strokeDasharray = pathLength + " " + pathLength

    // Offset the dashes so the it appears hidden entirely
    path.style.strokeDashoffset = pathLength

    // Jake Archibald says so
    // https://jakearchibald.com/2013/animated-line-drawing-svg/
    path.getBoundingClientRect()

    const callback = function (e) {
      // What % down is it?
      // https://stackoverflow.com/questions/2387136/cross-browser-method-to-determine-vertical-scroll-percentage-in-javascript/2387222#2387222
      // scrollTop === offsetTop                | percentage = 0
      // ScrollTop === offsetTop + offsetHeight | percentage = 1
      // if (
      //   document.documentElement.scrollTop + 20 <
      //   parentRef.current.offsetTop
      // ) {
      //   return
      // }

      const scrollPercentage =
        (document.documentElement.scrollTop -
          parentRef.current.offsetTop +
          window.innerHeight) /
        parentRef.current.offsetHeight

      var drawLength = pathLength * scrollPercentage
      // path.style.opacity = scrollPercentage

      // Draw in reverse
      path.style.strokeDashoffset = pathLength - drawLength

      // When complete, remove the dash array, otherwise shape isn't quite sharp
      // Accounts for fuzzy math
      if (scrollPercentage >= 0.99) {
        path.style.strokeDasharray = "none"
      } else {
        path.style.strokeDasharray = pathLength + " " + pathLength
      }
    }

    window.addEventListener("scroll", callback)

    return () => {
      window.removeEventListener("scroll", callback)
    }
  }, [])

  return series.map(({ id, data, color }) => (
    <path
      ref={pathRef}
      key={id}
      d={lineGenerator(
        data.map(d => ({
          x: xScale(d.data.x),
          y: yScale(d.data.y),
        }))
      )}
      fill="none"
      stroke={color}
      style={{
        strokeWidth: 3,
        // opacity: 0,
      }}
    />
  ))
}

export const CustomRangeLineChart = ({
  data,
  title,
  sourceText,
  yLabel,
  xTooltipLabel,
  yTooltipLabel,
}) => {
  // const [isInViewport, targetRef] = useIsInViewport({ threshold: 50 })
  const ref = useRef(null)

  return (
    <div className="chart-wrapper" ref={ref}>
      <div className="chart">
        <FigureText text={title} />
        <ResponsiveLine
          enableGridX={false}
          data={data}
          curve="cardinal"
          margin={{ top: 50, right: 20, bottom: 50, left: 60 }}
          xScale={{
            type: "point",
          }}
          theme={theme}
          yScale={{
            type: "linear",
            min: "auto",
            max: "auto",
            stacked: true,
            reverse: false,
          }}
          axisBottom={{
            format: tick =>
              parseInt(tick.split("-")[1]) % 3 === 0 ? tick : "",
            legendPosition: 0,
            orient: "left",
            legendOffset: 100,
          }}
          axisTop={null}
          axisRight={null}
          axisLeft={{
            orient: "left",
            tickSize: 5,
            tickPadding: 5,
            legend: yLabel,
            tickRotation: 0,
            legendOffset: -40,
            legendPosition: "middle",
          }}
          colors={() => [themeColor]}
          pointSize={3}
          pointBorderWidth={2}
          pointLabel="y"
          pointLabelYOffset={-12}
          useMesh={true}
          legends={[
            {
              anchor: "bottom",
              direction: "column",
              justify: false,
              translateX: 0,
              translateY: 50,
              itemsSpacing: 0,
              itemDirection: "left-to-right",
              itemWidth: 80,
              itemHeight: 20,
              itemOpacity: 0.75,
              symbolSize: 12,
              symbolShape: "circle",
            },
          ]}
          layers={[
            "grid",
            "markers",
            "areas",
            props => <DashedLine {...props} parentRef={ref} />,
            "slices",
            "dots",
            "legends",
            "axes",
            "crosshair",
            "mesh",
          ]}
          tooltip={({ point: { data, color } }) =>
            tooltip({ data, xTooltipLabel, yTooltipLabel, color })
          }
        />
        <Source text={sourceText} />
      </div>
    </div>
  )
}

export const CustomTestLineChart = ({
  data,
  title,
  sourceText,
  yLabel,
  xTooltipLabel,
  yTooltipLabel,
}) => {
  // const [isInViewport, targetRef] = useIsInViewport({ threshold: 50 })
  // console.log("yo boii ", isInViewport)
  const ref = useRef(null)

  return (
    <div className="chart-wrapper" ref={ref}>
      <div className="chart">
        <FigureText text={title} />
        <ResponsiveLine
          enableGridX={false}
          data={data}
          curve="cardinal"
          margin={{ top: 50, right: 20, bottom: 50, left: 60 }}
          xScale={{
            type: "point",
          }}
          theme={theme}
          yScale={{
            type: "linear",
            min: "auto",
            max: "auto",
            stacked: true,
            reverse: false,
          }}
          axisBottom={{
            tickValues: 6,
            format: (tick, i) => {
              const year = tick.split("-")[1]
              const cond1 = parseInt(year) % 2 === 0
              const cond2 = tick.indexOf("Mar") > -1
              const newLabelEnd = year.substr(2, year.length)

              return cond1 && cond2
                ? `${tick.split("-")[0]}-${newLabelEnd}`
                : undefined
            },
            legendPosition: 0,
            orient: "left",
            legendOffset: 100,
          }}
          tooltip={({ point: { data, color } }) =>
            tooltip({ data, xTooltipLabel, yTooltipLabel, color })
          }
          axisTop={null}
          axisRight={null}
          axisLeft={{
            orient: "left",
            tickSize: 5,
            tickPadding: 5,
            legend: yLabel,
            tickRotation: 0,
            legendOffset: -40,
            legendPosition: "middle",
          }}
          colors={() => [themeColor]}
          pointSize={3}
          pointBorderWidth={2}
          pointLabel="y"
          pointLabelYOffset={-12}
          useMesh={true}
          legends={[
            {
              anchor: "bottom",
              direction: "column",
              justify: false,
              translateX: 0,
              translateY: 50,
              itemsSpacing: 0,
              itemDirection: "left-to-right",
              itemWidth: 80,
              itemHeight: 20,
              itemOpacity: 0.75,
              symbolSize: 12,
              symbolShape: "circle",
            },
          ]}
          layers={[
            "grid",
            "markers",
            "areas",
            props => <DashedLine {...props} parentRef={ref} />,
            "slices",
            "dots",
            "legends",
            "axes",
            "crosshair",
            "mesh",
          ]}
        />
        <Source text={sourceText} />
      </div>
    </div>
  )
}
